import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal, X, ChevronDown, Search } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/SEO";
import Pagination from "@/components/Pagination";

const PAGE_LIMIT = 48;

const SORTS = [
    { value: "popularity", label: "Popularity" },
    { value: "discount", label: "Discount: High to Low" },
    { value: "rating", label: "Rating" },
    { value: "name", label: "Name A-Z" },
];

const DISCOUNT_BANDS = [
    { id: "1-5", label: "1% – 5%", min: 0.01, max: 5 },
    { id: "6-10", label: "6% – 10%", min: 6, max: 10 },
    { id: "11-25", label: "11% – 25%", min: 11, max: 25 },
    { id: "26-plus", label: "26% & above", min: 26, max: 100 },
];

const ACCEPTANCE = [
    { id: "Online", label: "Online" },
    { id: "Offline", label: "Offline" },
    { id: "Both", label: "Both" },
];

export default function Catalog() {
    const [params, setParams] = useSearchParams();
    const [brands, setBrands] = useState([]);
    const [total, setTotal] = useState(0);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [brandSearch, setBrandSearch] = useState(params.get("q") || "");

    const q = params.get("q") || "";
    const category = params.get("category") || "";
    const sort = params.get("sort") || "popularity";
    const featured = params.get("featured") === "true";
    const trending = params.get("trending") === "true";
    const maxPrice = params.get("max_price");
    const discountBand = params.get("discount") || "";
    const acceptance = params.get("acceptance") || "";
    const pageNum = Math.max(1, parseInt(params.get("page") || "1", 10));

    useEffect(() => {
        (async () => {
            setLoading(true);
            const qs = new URLSearchParams();
            if (q) qs.set("q", q);
            if (category) qs.set("category", category);
            if (featured) qs.set("featured", "true");
            if (trending) qs.set("trending", "true");
            if (maxPrice) qs.set("max_price", maxPrice);
            if (acceptance) qs.set("acceptance", acceptance);
            if (discountBand) {
                const band = DISCOUNT_BANDS.find((b) => b.id === discountBand);
                if (band) {
                    qs.set("min_discount", band.min);
                    qs.set("max_discount", band.max);
                }
            }
            qs.set("sort", sort);
            qs.set("page", String(pageNum));
            qs.set("limit", String(PAGE_LIMIT));
            const [b, c] = await Promise.all([
                api.get(`/brands?${qs.toString()}`),
                api.get("/categories"),
            ]);
            setBrands(b.data.items);
            setTotal(b.data.total);
            setCategories(c.data);
            setLoading(false);
        })();
    }, [q, category, sort, featured, trending, maxPrice, discountBand, acceptance, pageNum]);

    const setParam = (key, val) => {
        const np = new URLSearchParams(params);
        if (!val) np.delete(key);
        else np.set(key, val);
        // Any filter change resets page to 1
        if (key !== "page") np.delete("page");
        setParams(np);
    };

    const setPage = (p) => {
        const np = new URLSearchParams(params);
        if (p === 1) np.delete("page");
        else np.set("page", String(p));
        setParams(np);
    };

    const onBrandSearch = (e) => {
        e.preventDefault();
        setParam("q", brandSearch.trim());
    };

    const clearAll = () => {
        setBrandSearch("");
        setParams(new URLSearchParams());
    };

    const activeChips = useMemo(() => {
        const chips = [];
        if (q) chips.push({ k: "q", label: `"${q}"` });
        if (category) chips.push({ k: "category", label: category });
        if (discountBand) {
            const b = DISCOUNT_BANDS.find((x) => x.id === discountBand);
            if (b) chips.push({ k: "discount", label: `Disc ${b.label}` });
        }
        if (acceptance) chips.push({ k: "acceptance", label: acceptance });
        if (maxPrice) chips.push({ k: "max_price", label: `Under ₹${Number(maxPrice).toLocaleString("en-IN")}` });
        if (featured) chips.push({ k: "featured", label: "Featured" });
        if (trending) chips.push({ k: "trending", label: "Trending" });
        return chips;
    }, [q, category, discountBand, acceptance, maxPrice, featured, trending]);

    const pageTitle = useMemo(() => {
        if (q) return `Search: "${q}"`;
        if (category) return category + " Gift Cards";
        if (featured) return "Featured Brands";
        if (trending) return "Trending Now";
        if (maxPrice) return `Gifts Under ₹${Number(maxPrice).toLocaleString("en-IN")}`;
        return "All Brands";
    }, [q, category, featured, trending, maxPrice]);

    return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-6 md:py-10">
            <SEO
                title={pageTitle}
                description={`Shop ${total} ${category || "premium"} gift cards${q ? ` matching "${q}"` : ""} on LoyltyKart. Instant WhatsApp delivery, up to 12% off, 100% genuine — from Tanishq, MakeMyTrip, Nykaa, Amazon, Croma, Taj & more.`}
                path={category ? `/catalog?category=${encodeURIComponent(category)}` : "/catalog"}
                keywords={`${category || "gift cards"}, ${category ? category + " gift card online" : "buy gift cards india"}, instant digital voucher, whatsapp gift`}
                structuredData={breadcrumbSchema([
                    { name: "Home", path: "/" },
                    { name: "Catalog", path: "/catalog" },
                    ...(category ? [{ name: category, path: `/catalog?category=${encodeURIComponent(category)}` }] : []),
                ])}
            />
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.22em] text-[#6C6F7A] mb-2">Catalog</div>
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <h1 className="font-serif text-3xl md:text-5xl text-[#0B1437]">{pageTitle}</h1>
                    <p className="text-sm text-[#6C6F7A]">{total} brand{total !== 1 ? "s" : ""}</p>
                </div>
            </div>

            <div className="grid md:grid-cols-12 gap-6 md:gap-8">
                {/* Filters sidebar */}
                <aside className={`md:col-span-3 ${showFilters ? "block" : "hidden md:block"}`}>
                    <div className="bg-white border border-[#E5E0D5] rounded-2xl p-5 md:sticky md:top-24">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-serif text-lg text-rk-navy">Filters</h3>
                            <button onClick={() => setShowFilters(false)} className="p-1.5 md:hidden">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Brand search */}
                        <form onSubmit={onBrandSearch} className="mb-5">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-rk-muted" />
                                <input
                                    data-testid="filter-brand-search"
                                    type="text"
                                    placeholder="Search by brand"
                                    value={brandSearch}
                                    onChange={(e) => setBrandSearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 text-sm border border-rk-border rounded-md focus:outline-none focus:border-rk-navy bg-white"
                                />
                            </div>
                        </form>

                        {/* Discount bands */}
                        <div className="mb-5">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-rk-muted mb-2 font-semibold">Discount</div>
                            <div className="grid grid-cols-2 gap-2">
                                {DISCOUNT_BANDS.map((b) => (
                                    <button
                                        key={b.id}
                                        data-testid={`filter-disc-${b.id}`}
                                        onClick={() => setParam("discount", b.id === discountBand ? "" : b.id)}
                                        className={`text-xs px-3 py-2 rounded-md border transition ${
                                            b.id === discountBand
                                                ? "bg-rk-navy text-white border-rk-navy"
                                                : "border-rk-border hover:border-rk-navy text-rk-navy"
                                        }`}
                                    >
                                        {b.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Acceptance */}
                        <div className="mb-5">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-rk-muted mb-2 font-semibold">Redeemable</div>
                            <div className="grid grid-cols-3 gap-2">
                                {ACCEPTANCE.map((a) => (
                                    <button
                                        key={a.id}
                                        data-testid={`filter-accept-${a.id}`}
                                        onClick={() => setParam("acceptance", a.id === acceptance ? "" : a.id)}
                                        className={`text-xs px-2 py-2 rounded-md border transition ${
                                            a.id === acceptance
                                                ? "bg-rk-navy text-white border-rk-navy"
                                                : "border-rk-border hover:border-rk-navy text-rk-navy"
                                        }`}
                                    >
                                        {a.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Categories */}
                        <div className="mb-5">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-rk-muted mb-2 font-semibold">Categories</div>
                            <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
                                <button
                                    onClick={() => setParam("category", "")}
                                    data-testid="filter-cat-all"
                                    className={`w-full text-left text-sm px-2 py-1.5 rounded-md ${!category ? "bg-rk-navy text-white" : "hover:bg-rk-cream"}`}
                                >
                                    All categories <span className="float-right text-xs opacity-70">{categories.reduce((s, c) => s + c.count, 0)}</span>
                                </button>
                                {categories.map((c) => (
                                    <button
                                        key={c.name}
                                        data-testid={`filter-cat-${c.name}`}
                                        onClick={() => setParam("category", c.name === category ? "" : c.name)}
                                        className={`w-full text-left text-sm px-2 py-1.5 rounded-md ${c.name === category ? "bg-rk-navy text-white" : "hover:bg-rk-cream"}`}
                                    >
                                        {c.name} <span className="float-right text-xs opacity-70">{c.count}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Quick filters */}
                        <div className="mb-5">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-rk-muted mb-2 font-semibold">Quick filters</div>
                            <label className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                                <input data-testid="filter-featured" type="checkbox" checked={featured} onChange={(e) => setParam("featured", e.target.checked ? "true" : "")} />
                                Featured
                            </label>
                            <label className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                                <input data-testid="filter-trending" type="checkbox" checked={trending} onChange={(e) => setParam("trending", e.target.checked ? "true" : "")} />
                                Trending
                            </label>
                        </div>

                        {/* Max Price */}
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-rk-muted mb-2 font-semibold">Max Price</div>
                            <div className="flex flex-wrap gap-2">
                                {[500, 1000, 2000, 5000, 10000].map((p) => (
                                    <button
                                        data-testid={`filter-price-${p}`}
                                        key={p}
                                        onClick={() => setParam("max_price", String(maxPrice) === String(p) ? "" : p)}
                                        className={`text-xs px-3 py-1.5 rounded-full border ${String(maxPrice) === String(p) ? "bg-rk-navy text-white border-rk-navy" : "border-rk-border hover:border-rk-navy"}`}
                                    >
                                        Under ₹{p.toLocaleString("en-IN")}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {activeChips.length > 0 && (
                            <button
                                onClick={clearAll}
                                data-testid="filter-clear-all"
                                className="mt-5 w-full text-xs uppercase tracking-[0.22em] text-rk-gold-deep hover:text-rk-navy font-semibold py-2 border border-rk-gold/40 hover:border-rk-navy rounded-md transition"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                </aside>

                <div className="md:col-span-9">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => setShowFilters(true)}
                            data-testid="show-filters-btn"
                            className="md:hidden rk-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2"
                        >
                            <SlidersHorizontal className="w-4 h-4" /> Filters
                        </button>
                        <div className="ml-auto">
                            <label className="text-xs text-rk-muted uppercase tracking-wider mr-2">Sort by</label>
                            <div className="inline-flex relative">
                                <select
                                    data-testid="sort-select"
                                    value={sort}
                                    onChange={(e) => setParam("sort", e.target.value)}
                                    className="appearance-none text-sm border border-rk-border rounded-full pl-4 pr-9 py-2 bg-white focus:outline-none focus:border-rk-navy"
                                >
                                    {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                                <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* Active filter chips */}
                    {activeChips.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-2">
                            {activeChips.map((c) => (
                                <button
                                    key={c.k}
                                    onClick={() => setParam(c.k, "")}
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-rk-navy text-white hover:bg-rk-navy-2"
                                >
                                    {c.label} <X className="w-3 h-3" />
                                </button>
                            ))}
                        </div>
                    )}

                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="aspect-[4/5] rk-shimmer rounded-2xl" />
                            ))}
                        </div>
                    ) : brands.length === 0 ? (
                        <div className="py-20 text-center">
                            <p className="font-serif text-2xl mb-2">No brands found</p>
                            <p className="text-sm text-rk-muted">Try a different search or filter.</p>
                            <button onClick={clearAll} className="rk-btn-primary inline-block mt-6 !text-sm">Reset filters</button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 rk-fade-up">
                                {brands.map((b) => <ProductCard key={b.id} brand={b} />)}
                            </div>
                            <Pagination page={pageNum} total={total} limit={PAGE_LIMIT} onChange={setPage} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
